import Handsontable from 'handsontable';
import { BaseRenderer } from 'handsontable/renderers/base';
import { _isEmpty, _isNil, _isNotEmpty, _notNil } from '@/littledash';
import { errorToast } from '@/helpers';
import ReactDOM, { Root } from 'react-dom/client';
import { StrictMode } from 'react';
import {
  TimepointAction,
  timepointComparator,
  TimepointComponent,
} from '@/components/UI/SpreadSheet/editors/TimepointComponent';
import styles from './TimepointEditor.module.scss';
import { DateUtils, DurationValidationErrorReason, DurationValidationResult } from '@/utils/Date.utils';
import InVivoError from '@/model/InVivoError';
import { CellValue, RangeType } from 'handsontable/common';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { fromInVivoValidator, InVivoValidator } from '@/support/hotValidator.util';
import { createTimepointErrorMessage } from './TimepointErrorHandler';
import { valueToArray } from '@/utils/array.utils';

const nextCellCoordinates = (
  row: number,
  column: number,
  keypress: { key: 'Enter' | 'Tab'; shiftKey: boolean }
): {
  row: number;
  column: number;
} => {
  switch (keypress.key) {
    case 'Enter':
      return { row: keypress.shiftKey ? row - 1 : row + 1, column };
    case 'Tab':
      return { row, column: keypress.shiftKey ? column - 1 : column + 1 };
  }
  throw new InVivoError(`Unexpected key ${keypress.key}`, { slug: 'timepoint-editor-next-cell' });
};

// Re-export valueToArray from utils for backward compatibility
export { valueToArray };

export const timepointAfterPasteHandler = function (
  this: Handsontable,
  data: CellValue[][],
  coords: RangeType[]
): void {
  if (_isEmpty(coords) || _isEmpty(data)) {
    return;
  }

  try {
    const dataRows = data.length;
    const dataCols = data[0]?.length ?? 0;

    const immediateUpdates: Array<[number, number, CellValue]> = [];
    const asyncProcessingCells: Array<{
      row: number;
      col: number;
      validDurations: Array<string>;
      invalidValues: Array<string>;
    }> = [];

    for (const { startRow, startCol, endRow, endCol } of coords) {
      const actualEndRow = Math.max(endRow, startRow + dataRows - 1);
      for (let row = startRow; row <= actualEndRow; row++) {
        for (let col = startCol; col <= endCol; col++) {
          const cellMeta = this.getCellMeta(row, col);
          if (cellMeta.type === TimepointEditor.type || cellMeta.editor === TimepointEditor) {
            const dataRowIndex = (row - startRow) % dataRows;
            const dataColIndex = (col - startCol) % dataCols;
            const pasteValue = data[dataRowIndex]?.[dataColIndex];

            if (_isNil(pasteValue)) {
              continue;
            }

            const processedValues = valueToArray(pasteValue);
            const allow_zero = cellMeta.allow_zero === true;
            const allow_negative = cellMeta.allow_negative === true;

            if (_isEmpty(processedValues)) {
              continue;
            }

            const { validDurations, invalidValues, rangeErrorsByType } = processedValues.reduce<{
              validDurations: Array<string>;
              invalidValues: Array<string>;
              rangeErrorsByType: Partial<Record<DurationValidationErrorReason, Array<string>>>;
            }>(
              (acc, value) => {
                const validation = DateUtils.validateDurationRange(value, { allow_zero, allow_negative });

                if (validation.valid) {
                  acc.validDurations.push(value);
                } else if (validation.reason === 'error_format') {
                  acc.invalidValues.push(value);
                } else {
                  // Group range errors by type immediately
                  acc.rangeErrorsByType[validation.reason] = [
                    ...(acc.rangeErrorsByType[validation.reason] ?? []),
                    value,
                  ];
                }
                return acc;
              },
              { validDurations: [], invalidValues: [], rangeErrorsByType: {} }
            );

            const rangeErrorsByTypeEntries = Object.entries(rangeErrorsByType) as Array<
              [DurationValidationErrorReason, Array<string>]
            >;
            // Handle range validation errors immediately
            if (rangeErrorsByTypeEntries.length > 0) {
              rangeErrorsByTypeEntries.forEach(([errorType, values]) => {
                errorToast(
                  createTimepointErrorMessage(errorType, {
                    values: values,
                    originalValues: values,
                  })
                );
              });
            }

            if (_isNotEmpty(invalidValues)) {
              asyncProcessingCells.push({ row, col, validDurations, invalidValues });
            } else if (_isNotEmpty(validDurations)) {
              immediateUpdates.push([row, col, validDurations.sort(timepointComparator)]);
            }
          }
        }
      }
    }

    if (_isNotEmpty(immediateUpdates)) {
      this.setDataAtCell(immediateUpdates, 'edit');
    }

    if (_isNotEmpty(asyncProcessingCells)) {
      asyncProcessingCells.forEach(({ row, col, validDurations, invalidValues }) => {
        Promise.allSettled(invalidValues.map((duration) => DateUtils.parseDuration(duration)))
          .then((results) => {
            const cellMeta = this.getCellMeta(row, col);
            const allow_zero = cellMeta.allow_zero === true;
            const allow_negative = cellMeta.allow_negative === true;
            const convertedValues: Array<string> = [];
            const failedValues: Array<string> = [];
            const rangeErrorsByType: Partial<
              Record<DurationValidationErrorReason, { parsedValues: Array<string>; originalValues: Array<string> }>
            > = {};

            for (let i = 0; i < results.length; i++) {
              const result = results[i];
              const originalValue = invalidValues[i];

              if (result.status === 'fulfilled') {
                const rangeValidation = DateUtils.validateDurationRange(result.value, { allow_zero, allow_negative });
                if (rangeValidation.valid) {
                  convertedValues.push(result.value);
                } else {
                  const errorKey = rangeValidation.reason;
                  rangeErrorsByType[errorKey] = {
                    parsedValues: [...(rangeErrorsByType[errorKey]?.parsedValues ?? []), result.value],
                    originalValues: [...(rangeErrorsByType[errorKey]?.originalValues ?? []), originalValue],
                  };
                }
              } else {
                failedValues.push(originalValue);
              }
            }

            if (_isNotEmpty(failedValues)) {
              errorToast(createTimepointErrorMessage('error_format', { values: failedValues }));
            }

            // Show separate error messages for each range error type
            Object.entries(rangeErrorsByType).forEach(([errorType, errorData]) => {
              errorToast(
                createTimepointErrorMessage(errorType as DurationValidationErrorReason, {
                  values: errorData.parsedValues,
                  originalValues: errorData.originalValues,
                })
              );
            });

            const finalValue = _isNotEmpty(convertedValues)
              ? [...new Set([...validDurations, ...convertedValues])].sort(timepointComparator)
              : validDurations;

            if (_isNotEmpty(finalValue)) {
              this.setDataAtCell(row, col, finalValue, 'edit');
            } else {
              this.setDataAtCell(row, col, [], 'edit');
            }
          })
          .catch((error) => {
            if (_isNotEmpty(validDurations)) {
              this.setDataAtCell(row, col, validDurations, 'edit');
            } else {
              this.setDataAtCell(row, col, [], 'edit');
            }
            ExceptionHandler.captureException(
              new InVivoError('Error during timepoint async processing', {
                slug: 'timepoint-paste-async-error',
                cause: error,
                level: 'warning',
              })
            );
          });
      });
    }
  } catch (error) {
    ExceptionHandler.captureException(
      new InVivoError('Error in timepoint paste handler', {
        slug: 'timepoint-paste-handler-error',
        cause: error,
        level: 'error',
      })
    );
  }
};

const timepointRenderer: BaseRenderer = (hotInstance, td, row, column, prop, value, cellProperties) => {
  Handsontable.renderers.BaseRenderer.call(null, hotInstance, td, row, column, prop, value, cellProperties);
  Handsontable.dom.empty(td);

  const arrayValue = valueToArray(value);

  if (_isNotEmpty(arrayValue)) {
    const chips = document.createElement('div');
    chips.classList.add(styles.Chips);
    arrayValue.forEach((timepointValue) => {
      try {
        const parsedValue = DateUtils.renderDuration(timepointValue, {
          format: 'short',
          zeros: true,
          defaultResponse: '',
        });
        if (_isNotEmpty(parsedValue)) {
          const chip = document.createElement('div');
          chip.classList.add(styles.Chip);
          const chipValue = document.createElement('div');
          chipValue.classList.add(styles.Value);
          chip.appendChild(chipValue);
          chip.innerText = parsedValue;
          chips.appendChild(chip);
        }
      } catch (e) {
        // intentionally empty
      }
    });
    td.appendChild(chips);
  }
};

export const timepointInVivoValidator: InVivoValidator = async (value, columnSettings) => {
  const required = columnSettings.required === true;
  const allow_zero = columnSettings.allow_zero === true;
  const allow_negative = columnSettings.allow_negative === true;
  const arrayValue = valueToArray(value);

  if (_isEmpty(arrayValue)) {
    if (required) {
      return { valid: false, message: 'This field is required' };
    }
    return { valid: true };
  }

  // First check format validation
  const formatValidationResults = arrayValue.map<{ value: string } & DurationValidationResult>((tpValue) => {
    if (typeof tpValue === 'string') {
      return { value: tpValue, ...DateUtils.validateDurationRange(tpValue, { allow_zero, allow_negative }) };
    }
    return { value: tpValue, valid: false, reason: 'error_format' };
  });

  const rangeInvalidValues = formatValidationResults.filter((r) => !r.valid);
  if (_isNotEmpty(rangeInvalidValues)) {
    // Group range validation errors by type and report the first one
    const errorsByType = rangeInvalidValues.reduce<Partial<Record<DurationValidationErrorReason, Array<string>>>>(
      (acc, result) => ({ ...acc, [result.reason]: [...(acc[result.reason] ?? []), result.value] }),
      {}
    );
    const firstError = (Object.entries(errorsByType) as Array<[DurationValidationErrorReason, Array<string>]>).sort(
      (left, right) => right[1].length - left[1].length
    )?.[0];
    if (_notNil(firstError)) {
      return {
        valid: false,
        message: createTimepointErrorMessage(firstError[0], { values: firstError[1], originalValues: firstError[1] }),
      };
    }
  }
  return { valid: true };
};

export class TimepointEditor extends Handsontable.editors.BaseEditor {
  static type = 'timepoint';

  static register(type = TimepointEditor.type) {
    Handsontable.cellTypes.registerCellType(type, {
      editor: TimepointEditor,
      renderer: timepointRenderer,
      validator: fromInVivoValidator(timepointInVivoValidator),
    });
  }

  displayed = false;
  timepointContainer: HTMLDivElement | null = null;
  timepointOverlay: HTMLDivElement | null = null;
  #reactDomRoot: Root | null = null;
  currentValue: Array<string> = [];
  originalValue: Array<string> = [];

  init() {
    this.instance.addHook('afterDestroy', () => this.destroyElements());
  }

  prepare(
    row: number,
    col: number,
    prop: string | number,
    td: HTMLTableCellElement,
    originalValue: any,
    cellProperties: Handsontable.CellProperties
  ) {
    const arrayValue = valueToArray(originalValue);
    super.prepare(row, col, prop, td, arrayValue, cellProperties);
    this.originalValue = [...arrayValue];
    this.currentValue = [...arrayValue];
  }

  beginEditing(initialValue?: any, event?: Event) {
    if (_notNil(initialValue)) {
      this.setValue(initialValue);
    }
    super.beginEditing(initialValue, event);
    this.open(event);
  }

  createElements() {
    this.timepointContainer = this.hot.rootDocument.createElement('div');
    this.timepointContainer.classList.add(styles.TimepointContainer);
    this.timepointContainer.setAttribute('data-test-component', 'TimepointEditor');
    this.timepointContainer.setAttribute('data-test-element', 'container');
    this.timepointContainer.style.display = 'none';

    this.timepointOverlay = this.hot.rootDocument.createElement('div');
    this.timepointOverlay.classList.add(styles.TimepointOverlay);
    this.timepointOverlay.setAttribute('data-test-element', 'overlay');
    this.timepointContainer.appendChild(this.timepointOverlay);
    this.hot.rootDocument.body.appendChild(this.timepointContainer);

    const handleMouseDown = (event: MouseEvent) => {
      if (_notNil(this.timepointContainer) && event.target === this.timepointContainer) {
        this.finishEditing(false, false, () => {
          const { row, col } = this.cellProperties;
          this.hot.selectCell(row, col);
        });
      } else {
        event.stopImmediatePropagation();
      }
    };

    this.timepointContainer.addEventListener('mousedown', handleMouseDown, {
      passive: false,
    });
  }

  destroyElements() {
    if (_notNil(this.timepointContainer)) {
      this.#reactDomRoot?.unmount();
      this.#reactDomRoot = null;
      Handsontable.dom.empty(this.timepointContainer);
      this.timepointContainer.remove();
      this.timepointContainer = null;
    }
  }

  getValue(): Array<string> {
    return this.currentValue;
  }

  setValue(newValue: Array<string> | string | null) {
    this.currentValue = valueToArray(newValue);

    if (this.isOpened() && _notNil(this.#reactDomRoot)) {
      window.requestAnimationFrame(() => {
        this.render();
      });
    }
  }

  open(event?: Event) {
    this.display();
  }

  close() {
    this.display(false);
  }

  focus() {
    // Focus is handled by the React component
  }

  isOpened() {
    return this.displayed;
  }

  saveValue() {
    this.instance.setDataAtCell(this.row, this.col, this.currentValue, 'edit');
  }

  finishEditing(restoreOriginalValue = false, ctrlDown = false, callback?: () => void) {
    if (this.isOpened()) {
      if (restoreOriginalValue && _notNil(this.originalValue)) {
        this.setValue(this.originalValue);
      } else {
        this.saveValue();
      }
    }

    super.finishEditing(restoreOriginalValue, ctrlDown, callback);
  }

  cancelChanges() {
    this.currentValue = [...this.originalValue];
    super.cancelChanges();
  }

  discardEditor() {
    this.display(false);
  }

  display(display = true) {
    this.displayed = display;

    if (_isNil(this.timepointContainer) || _isNil(this.timepointOverlay)) {
      this.createElements();
    }

    if (display) {
      const offset = this.TD.getBoundingClientRect();
      (this.timepointOverlay as HTMLDivElement).style.top = `${offset.top}px`;
      (this.timepointOverlay as HTMLDivElement).style.left = `${offset.left}px`;
      (this.timepointOverlay as HTMLDivElement).style.width = `${offset.width}px`;
      (this.timepointContainer as HTMLDivElement).style.display = 'block';
      this.render();
    } else {
      (this.timepointContainer as HTMLDivElement).style.display = 'none';
      window.requestAnimationFrame(() => {
        this.#reactDomRoot?.unmount();
        this.#reactDomRoot = null;
        Handsontable.dom.empty(this.timepointOverlay as HTMLDivElement);
      });
    }
  }

  actionHandler(action: TimepointAction) {
    switch (action.type) {
      case 'update': {
        this.setValue(action.value);
        if (action.close) {
          if (_notNil(action.keypress)) {
            const currentRow = this.row;
            const currentCol = this.col;
            const { row: nextRow, column: nextColumn } = nextCellCoordinates(currentRow, currentCol, action.keypress);
            this.close();
            this.instance.setDataAtCell(currentRow, currentCol, this.currentValue, 'edit');
            setTimeout(() => {
              this.hot.selectCell(nextRow, nextColumn);
            }, 10);
          } else {
            this.finishEditing();
            this.display(false);
          }
        }
        break;
      }
      case 'close': {
        this.finishEditing();
        this.display(false);
        break;
      }
    }
  }

  render() {
    if (
      this.isOpened() &&
      _notNil(this.cellProperties) &&
      _notNil(this.timepointContainer) &&
      _notNil(this.timepointOverlay) &&
      this.timepointContainer?.style?.display === 'block'
    ) {
      if (_isNil(this.#reactDomRoot)) {
        this.#reactDomRoot = ReactDOM.createRoot(this.timepointOverlay);
      }
      this.#reactDomRoot.render(
        <StrictMode>
          <TimepointComponent
            timepoints={this.currentValue}
            handleAction={(action) => this.actionHandler(action)}
            isSingleValueOnly={this.cellProperties.singleValue === true}
          />
        </StrictMode>
      );
    }
  }
}
